import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PhoneOff } from 'lucide-react';

const BUBBLE_SIZE = 68;
const END_SIZE = 32;
const EDGE_MARGIN = 12;
const POSITION_STORAGE_KEY = 'allure.callPipBubble.pos.v1';

const formatDuration = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const statusLabel = (status, durationSeconds) => {
  if (status === 'active') return formatDuration(durationSeconds);
  if (status === 'calling') return 'Calling…';
  if (status === 'reconnecting') return 'Reconnect…';
  return 'Connecting…';
};

const initialsFor = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '•';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const readStoredPosition = () => {
  try {
    const raw = window.localStorage?.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
  } catch { /* noop */ }
  return null;
};

const clampWithinViewport = (x, y) => {
  const vw = window.visualViewport?.width || window.innerWidth || 360;
  const vh = window.visualViewport?.height || window.innerHeight || 640;
  const safeTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0', 10) || 0;
  const safeBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0', 10) || 0;
  const minX = EDGE_MARGIN;
  const minY = EDGE_MARGIN + safeTop;
  const maxX = Math.max(minX, vw - BUBBLE_SIZE - EDGE_MARGIN);
  const maxY = Math.max(minY, vh - BUBBLE_SIZE - EDGE_MARGIN - safeBottom);
  return { x: Math.min(Math.max(x, minX), maxX), y: Math.min(Math.max(y, minY), maxY) };
};

const defaultPosition = () => {
  const vw = window.visualViewport?.width || window.innerWidth || 360;
  const vh = window.visualViewport?.height || window.innerHeight || 640;
  return clampWithinViewport(vw - BUBBLE_SIZE - EDGE_MARGIN, vh - BUBBLE_SIZE - EDGE_MARGIN - 80);
};

/**
 * Floating draggable PiP bubble shown while a call is minimized.
 * - Tap the bubble -> restore the full call screen.
 * - Tap the small red icon -> hang up.
 * - Drag to reposition; last position is persisted and clamped on resize.
 */
export const CallPipBubble = ({
  open,
  name,
  image,
  status,
  durationSeconds = 0,
  onExpand,
  onEnd,
  dataTestId = 'call-pip-bubble',
}) => {
  const [position, setPosition] = useState(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const stored = readStoredPosition();
    const base = stored || defaultPosition();
    return clampWithinViewport(base.x, base.y);
  });
  const draggedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => setPosition((prev) => clampWithinViewport(prev.x, prev.y));
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [open]);

  const dragBounds = useMemo(() => {
    const clamped = clampWithinViewport(0, 0);
    const vw = window.visualViewport?.width || window.innerWidth || 360;
    const vh = window.visualViewport?.height || window.innerHeight || 640;
    const safeBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0', 10) || 0;
    return {
      left: clamped.x,
      top: clamped.y,
      right: Math.max(clamped.x, vw - BUBBLE_SIZE - EDGE_MARGIN),
      bottom: Math.max(clamped.y, vh - BUBBLE_SIZE - EDGE_MARGIN - safeBottom),
    };
  // Recompute on every render so viewport resizes (keyboard, rotation) stay in sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.x, position.y, open]);

  const avatar = useMemo(() => {
    if (image) {
      return (
        <img
          src={image}
          alt=""
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
        />
      );
    }
    return (
      <span style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF', letterSpacing: 0.5 }}>
        {initialsFor(name)}
      </span>
    );
  }, [image, name]);

  const handleTap = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    onExpand?.();
  };

  const handleEndClick = (event) => {
    event.stopPropagation();
    onEnd?.();
  };

  const isActive = status === 'active';
  const ringColor = status === 'reconnecting' ? '#F2B33F' : '#35D178';

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="call-pip-bubble"
          data-testid={dataTestId}
          role="button"
          tabIndex={0}
          aria-label={`Ongoing call with ${name}. Tap to return.`}
          drag
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={dragBounds}
          onDragStart={() => { draggedRef.current = true; }}
          onDragEnd={(_, info) => {
            const next = clampWithinViewport(position.x + info.offset.x, position.y + info.offset.y);
            setPosition(next);
            try { window.localStorage?.setItem(POSITION_STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
            // Keep draggedRef true for a tick so the click handler suppresses the ensuing tap.
            setTimeout(() => { draggedRef.current = false; }, 50);
          }}
          onClick={handleTap}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onExpand?.(); } }}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, x: position.x, y: position.y }}
          exit={{ scale: 0.6, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 32 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: BUBBLE_SIZE,
            height: BUBBLE_SIZE,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2A1A3E 0%, #1B1230 100%)',
            boxShadow: '0 18px 36px rgba(10, 5, 30, 0.55), 0 0 0 2px rgba(255,255,255,0.08) inset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            touchAction: 'none',
            zIndex: 12600,
            userSelect: 'none',
            fontFamily: "'Inria Serif', serif",
          }}
          whileTap={{ cursor: 'grabbing', scale: 0.96 }}
        >
          {/* Pulsing status ring */}
          <motion.span
            aria-hidden
            animate={isActive ? { boxShadow: [`0 0 0 0 ${ringColor}66`, `0 0 0 10px ${ringColor}00`] } : { boxShadow: `0 0 0 2px ${ringColor}` }}
            transition={isActive ? { duration: 1.6, repeat: Infinity, ease: 'easeOut' } : { duration: 0.2 }}
            style={{
              position: 'absolute',
              inset: 2,
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              width: BUBBLE_SIZE - 10,
              height: BUBBLE_SIZE - 10,
              borderRadius: '50%',
              background: '#3A2559',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              color: '#FFFFFF',
            }}
          >
            {avatar}
          </div>

          {/* Timer / status pill underneath the avatar */}
          <span
            data-testid={`${dataTestId}-status`}
            style={{
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '2px 8px',
              borderRadius: 999,
              background: '#120A24',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#FFFFFF',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {statusLabel(status, durationSeconds)}
          </span>

          {/* End-call quick action */}
          <button
            type="button"
            data-testid={`${dataTestId}-end`}
            onClick={handleEndClick}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="End call"
            style={{
              position: 'absolute',
              top: -10,
              right: -10,
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'transparent',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden
              style={{
                width: END_SIZE,
                height: END_SIZE,
                borderRadius: '50%',
                background: '#E94C57',
                border: '2px solid #1B1230',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px rgba(233,76,87,0.45)',
              }}
            >
              <PhoneOff size={14} />
            </span>
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default CallPipBubble;
